import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import apiService from '../services/apiService';

const Topbar = () => {
  const [user, setUser] = useState(null);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [notificationCount, setNotificationCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [loadingNotifications, setLoadingNotifications] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Get user information from localStorage or API
    const currentUser = apiService.getCurrentUser();
    console.log({currentUser})
    setUser(currentUser);
  }, []);

  // Function to fetch notifications
  const fetchNotifications = async () => {
    setLoadingNotifications(true);
    try {
      const data = await apiService.getNotifications(1, 30);
      setNotifications(data.results || []);
    } catch (error) {
      console.error('Error fetching notifications:', error);
      setNotifications([]);
    } finally {
      setLoadingNotifications(false);
    }
  };

  // Function to mark all notifications as read
  const handleMarkAllAsRead = async () => {
    try {
      await apiService.markAllNotificationsAsRead();
      setNotificationCount(0);
      // Update notifications to mark them as read
      setNotifications(prev => prev.map(notif => ({ ...notif, is_read: true })));
      // Dispatch event to update other components
       window.dispatchEvent(new CustomEvent('notificationCountUpdate', { detail: { unread_count: 0 } }));
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  };

  // Function to mark specific notification as read
  const handleMarkAsRead = async (notificationId) => {
    try {
      await apiService.markNotificationAsRead(notificationId);
      // Update local state
      setNotifications(prev => prev.map(notif => 
        notif.id === notificationId ? { ...notif, is_read: true } : notif
      ));
      // Update count
      const newCount = Math.max(0, notificationCount - 1);
      setNotificationCount(newCount);
      // Dispatch event to update other components
      window.dispatchEvent(new CustomEvent('notificationCountUpdate', { detail: { unread_count: newCount } }));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  // Function to clear all read notifications
  const handleClearNotifications = async () => {
    try {
      await apiService.deleteReadNotifications();
      // Remove read notifications from local state
      setNotifications(prev => prev.filter(notif => !notif.is_read));
      // Refresh notifications to get updated list
      await fetchNotifications();
    } catch (error) {
      console.error('Error clearing notifications:', error);
    }
  };

  // Listen for notification count updates
  useEffect(() => {
    const handleNotificationUpdate = (event) => {
      if (event.detail && event.detail.unread_count !== undefined) {
        setNotificationCount(event.detail.unread_count);
        // Refresh notifications when count updates
        fetchNotifications();
      }
    };

    window.addEventListener('notificationCountUpdate', handleNotificationUpdate);

    // Initial fetch of notification count and notifications
    const fetchInitialData = async () => {
      try {
        const data = await apiService.getUnreadNotificationsCount();
        setNotificationCount(data.unread_count || 0);
        await fetchNotifications();
      } catch (error) {
        console.error('Error fetching initial notification data:', error);
      }
    };

    fetchInitialData();

    return () => {
      window.removeEventListener('notificationCountUpdate', handleNotificationUpdate);
    };
  }, []);

  const handleLogout = async () => {
    setIsLoggingOut(true);
    try {
      await apiService.logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
      // Even if logout API fails, clear local storage and redirect
      navigate('/login');
    } finally {
      setIsLoggingOut(false);
    }
  };

  const handleClearCache = async () => {
    try {
      // Clear Cache Storage
      if ('caches' in window) {
        const keys = await caches.keys();
        await Promise.all(keys.map((key) => caches.delete(key)));
      }

      // Unregister Service Workers
      if ('serviceWorker' in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map((reg) => reg.unregister()));
      }

      // Clear IndexedDB databases (best-effort)
      if (window.indexedDB) {
        if (indexedDB.databases) {
          const dbs = await indexedDB.databases();
          await Promise.all(
            dbs
              .filter((db) => db && db.name)
              .map((db) =>
                new Promise((resolve) => {
                  const req = indexedDB.deleteDatabase(db.name);
                  req.onsuccess = req.onerror = req.onblocked = () => resolve();
                })
              )
          );
        } else {
          const commonDbNames = ['keyval-store', 'workbox-expiration', 'workbox-cache', 'localforage', 'persist:root'];
          await Promise.all(
            commonDbNames.map(
              (name) =>
                new Promise((resolve) => {
                  const req = indexedDB.deleteDatabase(name);
                  req.onsuccess = req.onerror = req.onblocked = () => resolve();
                })
            )
          );
        }
      }

      // Clear Local and Session Storage
      try { localStorage.clear(); } catch {}
      try { sessionStorage.clear(); } catch {}
    } catch (e) {
      console.error('Error while clearing caches/storage:', e);
    } finally {
      // Force a reload with cache-busting
      const baseUrl = window.location.origin + window.location.pathname;
      window.location.replace(`${baseUrl}?cb=${Date.now()}`);
    }
  };

  const getUserInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const getRoleBadgeColor = (roleName) => {
    switch (roleName) {
      case 'super_admin':
        return 'badge-danger';
      case 'MANAGER':
        return 'badge-warning';
      case 'TEAM':
        return 'badge-info';
      default:
        return 'badge-secondary';
    }
  };

  const getRoleDisplayName = (roleName) => {
    switch (roleName) {
      case 'super_admin':
        return 'Super Admin';
      case 'MANAGER':
        return 'Manager';
      case 'TEAM':
        return 'Team Member';
      default:
        return 'User';
    }
  };
  
  return (
    <nav className="navbar navbar-expand navbar-light bg-white topbar static-top shadow">
      {/* Sidebar Toggle (Topbar) */}
      <button id="sidebarToggleTop" className="btn btn-link d-md-none rounded-circle mr-3">
        <i className="fa fa-bars"></i>
      </button>

      

      {/* Topbar Navbar */}
      <ul className="navbar-nav ml-auto">
        {/* Nav Item - Search Dropdown (Visible Only XS) */}
        <li className="nav-item dropdown no-arrow d-sm-none">
          <a
            className="nav-link dropdown-toggle"
            href="#"
            id="searchDropdown"
            role="button"
            data-toggle="dropdown"
            aria-haspopup="true"
            aria-expanded="false"
          >
            <i className="fas fa-search fa-fw"></i>
          </a>
          {/* Dropdown - Messages */}
          <div
            className="dropdown-menu dropdown-menu-right p-3 shadow animated--grow-in"
            aria-labelledby="searchDropdown"
          >
            <form className="form-inline mr-auto w-100 navbar-search">
              <div className="input-group">
                <input
                  type="text"
                  className="form-control bg-light border-0 small"
                  placeholder="Search for..."
                  aria-label="Search"
                  aria-describedby="basic-addon2"
                />
                <div className="input-group-append">
                  <button className="btn btn-primary" type="button">
                    <i className="fas fa-search fa-sm"></i>
                  </button>
                </div>
              </div>
            </form>
          </div>
        </li>

        {/* Nav Item - Notifications */}
        <li className="nav-item dropdown no-arrow mx-1">
          <a
            className="nav-link dropdown-toggle"
            href="#"
            id="alertsDropdown"
            role="button"
            data-toggle="dropdown"
            aria-haspopup="true"
            aria-expanded="false"
          >
            <i className="fas fa-bell fa-fw"></i>
            {notificationCount > 0 && (
              <span className="badge badge-success badge-counter" style={{marginRight:"-2px", marginTop:"-16px"}}>{notificationCount}</span>
            )}
          </a>
          {/* Dropdown - Notifications */}
          <div
            className="dropdown-list dropdown-menu dropdown-menu-right shadow animated--grow-in"
            aria-labelledby="alertsDropdown"
            style={{ 
              minWidth: '400px', 
              maxHeight: '450px', 
              overflowY: 'auto',
              border: 'none',
              borderRadius: '12px',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
            }}
          >
            <div 
              className="dropdown-header text-center"
              style={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                padding: '1rem 1.25rem',
                borderRadius: '12px 12px 0 0',
                margin: '0',
                border: 'none',
                position: 'sticky',
                top: '0',
                zIndex: '10'
              }}
            >
              <span style={{ fontWeight: '600', fontSize: '0.9rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Notifications Center</span>
            </div>
            
            {loadingNotifications ? (
              <div className="dropdown-item text-center" style={{ padding: '2rem' }}>
                <i className="fas fa-spinner fa-spin" style={{ fontSize: '1.5rem', color: '#667eea' }}></i>
                <div style={{ marginTop: '0.5rem', color: '#6c757d', fontSize: '0.9rem' }}>Loading notifications...</div>
              </div>
            ) : notifications.length > 0 ? (
              <>
                {notifications.slice(0, 30).map((notification) => (
                  <div 
                    key={notification.id} 
                    className="dropdown-item d-flex align-items-start"
                    style={{ 
                      padding: '1rem 1.25rem', 
                      borderBottom: '1px solid #f1f3f4',
                      background: !notification.is_read ? 'linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%)' : 'white',
                      transition: 'all 0.3s ease',
                      cursor: 'default'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.background = !notification.is_read 
                        ? 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)'
                        : '#f8f9fa';
                      e.target.style.transform = 'translateX(2px)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.background = !notification.is_read 
                        ? 'linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%)'
                        : 'white';
                      e.target.style.transform = 'translateX(0)';
                    }}
                  >
                    <div className="flex-grow-1">
                      <div className="d-flex justify-content-between align-items-start">
                        <h6 className="mb-1" style={{ 
                          fontSize: '0.85rem', 
                          fontWeight: '600',
                          color: '#495057',
                          lineHeight: '1.3',
                          display: 'flex',
                          alignItems: 'center'
                        }}>
                          {!notification.is_read && (
                            <span 
                              style={{
                                display: 'inline-block',
                                width: '6px',
                                height: '6px',
                                background: 'linear-gradient(135deg, #28a745 0%, #20c997 100%)',
                                borderRadius: '50%',
                                marginRight: '8px',
                                boxShadow: '0 1px 3px rgba(40, 167, 69, 0.4)'
                              }}
                            >
                            </span>
                          )}
                          {notification.title}
                        </h6>
                        {!notification.is_read && (
                          <button 
                            className="btn btn-sm p-0 ml-2"
                            onClick={(e) => {
                              e.preventDefault();
                              handleMarkAsRead(notification.id);
                            }}
                            style={{ 
                              fontSize: '0.7rem', 
                              color: '#667eea',
                              background: 'rgba(102, 126, 234, 0.1)',
                              border: '1px solid rgba(102, 126, 234, 0.2)',
                              borderRadius: '4px',
                              padding: '2px 8px',
                              fontWeight: '600',
                              transition: 'all 0.3s ease'
                            }}
                            onMouseEnter={(e) => {
                              e.target.style.background = 'rgba(102, 126, 234, 0.2)';
                              e.target.style.transform = 'translateY(-1px)';
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.background = 'rgba(102, 126, 234, 0.1)';
                              e.target.style.transform = 'translateY(0)';
                            }}
                          >
                            Mark Read
                          </button>
                        )}
                      </div>
                      <p className="mb-2" style={{ 
                        fontSize: '0.75rem',
                        color: '#6c757d',
                        lineHeight: '1.4',
                        margin: '0.5rem 0'
                      }}>
                        {notification.message}
                      </p>
                      <div className="d-flex justify-content-between align-items-center">
                        <small style={{ 
                          color: '#858796',
                          fontSize: '0.7rem',
                          fontFamily: 'Monaco, Menlo, Ubuntu Mono, monospace',
                          background: '#f8f9fa',
                          padding: '2px 6px',
                          borderRadius: '4px'
                        }}>
                          {new Date(notification.created_at).toLocaleDateString()} {new Date(notification.created_at).toLocaleTimeString()}
                        </small>
                        
                      </div>
                    </div>
                  </div>
                ))}
                
              </>
            ) : (
              <div 
                className="dropdown-item text-center"
                style={{
                  padding: '3rem 2rem',
                  color: '#6c757d'
                }}
              >
                <i className="fas fa-bell-slash" style={{ fontSize: '2rem', opacity: '0.5', marginBottom: '1rem', display: 'block' }}></i>
                <div style={{ fontSize: '0.9rem', fontWeight: '500' }}>No notifications</div>
                <small style={{ color: '#858796' }}>You're all caught up!</small>
              </div>
            )}
            
            {/* Footer with action buttons */}
             {(notificationCount > 0 || notifications.some(notif => notif.is_read)) && (
               <div 
                 className="dropdown-footer d-flex justify-content-right"
                 style={{
                   position: 'sticky',
                   bottom: '0',
                   padding: '0.75rem 1rem',
                   background: '#f8f9fa',
                   borderTop: '1px solid #dee2e6',
                   borderRadius: '0 0 12px 12px',
                   gap: '10px',
                   zIndex: '10'
                 }}
               >
                 {notificationCount > 0 && (
                   <button 
                     className="btn btn-sm text-nowrap"
                     onClick={handleMarkAllAsRead}
                     style={{ 
                       fontSize: '0.75rem', 
                       padding: '8px 14px',
                       background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                       border: 'none',
                       color: 'white',
                       borderRadius: '6px',
                       fontWeight: '600',
                       transition: 'all 0.3s ease',
                       boxShadow: '0 2px 8px rgba(102, 126, 234, 0.3)',
                       whiteSpace: 'nowrap'
                     }}
                     onMouseEnter={(e) => {
                       e.target.style.transform = 'translateY(-2px)';
                       e.target.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.4)';
                     }}
                     onMouseLeave={(e) => {
                       e.target.style.transform = 'translateY(0)';
                       e.target.style.boxShadow = '0 2px 8px rgba(102, 126, 234, 0.3)';
                     }}
                   >
                     Mark All Read
                   </button>
                 )}
                 {notifications.some(notif => notif.is_read) && (
                    <button 
                      className="btn btn-sm text-nowrap"
                      onClick={handleClearNotifications}
                      style={{ 
                        fontSize: '0.75rem', 
                        padding: '8px 14px',
                        background: 'linear-gradient(135deg, #e74a3b 0%, #c0392b 100%)',
                        border: 'none',
                        color: 'white',
                        borderRadius: '6px',
                        fontWeight: '600',
                        transition: 'all 0.3s ease',
                        boxShadow: '0 2px 8px rgba(231, 74, 59, 0.3)',
                        whiteSpace: 'nowrap'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.transform = 'translateY(-2px)';
                        e.target.style.boxShadow = '0 4px 12px rgba(231, 74, 59, 0.4)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.transform = 'translateY(0)';
                        e.target.style.boxShadow = '0 2px 8px rgba(231, 74, 59, 0.3)';
                      }}
                    >
                      Clear Read
                    </button>
                  )}
               </div>
             )}
          </div>
        </li>

        <div className="topbar-divider d-none d-sm-block"></div>

        {/* Nav Item - User Information */}
        <li className="nav-item dropdown no-arrow">
          <a
            className="nav-link dropdown-toggle"
            href="#"
            id="userDropdown"
            role="button"
            data-toggle="dropdown"
            aria-haspopup="true"
            aria-expanded="false"
          >
            <span className="mr-2 d-none d-lg-inline text-gray-600 small">{(user?.name)}</span>
            <img
              className="img-profile rounded-circle"
              src="https://startbootstrap.github.io/startbootstrap-sb-admin-2/img/undraw_profile.svg"
              alt="User"
            />
          </a>
          {/* Dropdown - User Information */}
          <div
            className="dropdown-menu dropdown-menu-right shadow animated--grow-in"
            aria-labelledby="userDropdown"
          >
            <div className="dropdown-header">
              <div className="d-flex align-items-center">
                <div className="mr-3">
                  <div className="icon-circle bg-primary text-white d-flex align-items-center justify-content-center" style={{width: '40px', height: '40px', borderRadius: '50%'}}>
                    <span className="font-weight-bold">{getUserInitials(user?.name)}</span>
                  </div>
                </div>
                <div>
                  <div className="font-weight-bold text-gray-800">{user?.name || 'User'}</div>
                  <div className="small text-gray-600">{user?.email}</div>
                  {user?.role && (
                    <span className={`badge ${getRoleBadgeColor(user.role.name)} badge-sm mt-1`}>
                      {getRoleDisplayName(user.role.name)}
                    </span>
                  )}
                </div>
              </div>
            </div>
            <div className="dropdown-divider"></div>
            <button
              className="dropdown-item"
              onClick={handleClearCache}
              style={{ border: 'none', background: 'none', width: '100%', textAlign: 'left' }}
            >
              <i className="fas fa-sync-alt fa-sm fa-fw mr-2 text-gray-400"></i>
              Clear Cache
            </button>
            <button
              className="dropdown-item"
              onClick={handleLogout}
              disabled={isLoggingOut}
              style={{ border: 'none', background: 'none', width: '100%', textAlign: 'left' }}
            >
              <i className="fas fa-sign-out-alt fa-sm fa-fw mr-2 text-gray-400"></i>
              {isLoggingOut ? 'Logging out...' : 'Logout'}
            </button>
          </div>
        </li>
      </ul>
    </nav>
  );
};

export default Topbar;